import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { Orders, PaymentTypes } from '@grandvisionhq/api-client';
import { Selectors, getToken } from '@grandvisionhq/auth';
import { Context } from '@grandvisionhq/carts-v2';
import { useLocation, useLink } from '@grandvisionhq/frk-routing';
import { useSelector, ApiConfigSelectors } from '@grandvisionhq/state';
import { CreateOrderErrors, CheckoutErrorsMapping } from '../mappers/errors-mapping.js';
import { dispatchCartChangeEvent } from './use-cart-change-listener.js';
import { PaymentFlow } from '../models/payment/types.js';
import { CreateOrderError } from '../models/orders/create-order-error.js';
import { appendQueryParamToUrl } from '../utils/get-updated-url-with-query-params.js';

const useCreateOrder = ({ onError  } = {})=>{
    const [paymentFlow, setPaymentFlow] = React.useState(PaymentFlow.UNDEFINED);
    const [orderResponse1, setOrderResponse] = React.useState(undefined);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const apiConfig = useSelector(ApiConfigSelectors.getApiConfig);
    const { cart  } = Context.useCart();
    const { setContext  } = Context.useCartSettings();
    const isLoggedIn = useSelector(Selectors.isLoggedIn);
    const { replace  } = useLocation();
    const { href: paymentHref  } = useLink({
        route: 'checkout.payment',
        params: {
            cartId: ''
        }
    }) || {};
    /**
   * @description  call createOrder endpoint with payload and handle response with redirection
   * @argument orderPayload request object of type CreateOrderRequest
   */ const createOrder = React.useCallback(function() {
        var _ref = swcHelpers.asyncToGenerator(function*(orderPayload) {
            setLoading(true);
            const paymentMethod = orderPayload.paymentRequest.paymentMethod;
            let accessToken;
            // In case the user already has an account, he still has the option to change opt-ins during checkout
            if (isLoggedIn) {
                accessToken = yield getToken();
            }
            try {
                var ref;
                if (!apiConfig || !paymentMethod) {
                    console.error('Need apiConfig and a payment method to create an order');
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED
                    });
                }
                if (!cart) {
                    throw new CreateOrderError({
                        status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                        label: CheckoutErrorsMapping.CartNotFound
                    });
                }
                setError(null);
                const orderResponse = yield Orders.create(apiConfig.ordersUrl, orderPayload, accessToken);
                setOrderResponse(swcHelpers.objectSpread({}, orderResponse));
                const { status  } = orderResponse === null || orderResponse === void 0 ? void 0 : (ref = orderResponse.paymentResponse) === null || ref === void 0 ? void 0 : ref.result;
                switch(status){
                    case PaymentTypes.PaymentResult.Redirect:
                        var ref1, ref2;
                        if (((ref1 = orderResponse.paymentResponse) === null || ref1 === void 0 ? void 0 : (ref2 = ref1.redirect) === null || ref2 === void 0 ? void 0 : ref2.method) !== 'GET') {
                            throw new CreateOrderError({
                                status: CreateOrderErrors.UNSUPPORTED_REDIRECT_METHOD
                            });
                        }
                        setPaymentFlow(PaymentFlow.REDIRECT);
                        dispatchCartChangeEvent(orderPayload.cartId);
                        replace(orderResponse.paymentResponse.redirect.url);
                        break;
                    case PaymentTypes.PaymentResult.Pending:
                        var ref3, ref4;
                        if (((ref3 = orderResponse.paymentResponse) === null || ref3 === void 0 ? void 0 : (ref4 = ref3.resultDetails) === null || ref4 === void 0 ? void 0 : ref4.type) === 'qrCode') {
                            setPaymentFlow(PaymentFlow.QR_CODE);
                            break;
                        }
                    case PaymentTypes.PaymentResult.Pending:
                    case PaymentTypes.PaymentResult.Success:
                    case PaymentTypes.PaymentResult.Approved:
                        if (!orderResponse.orderDataToken || !cart.id) {
                            throw new CreateOrderError({
                                status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                                label: CheckoutErrorsMapping.TryAgain
                            });
                        }
                        setPaymentFlow(PaymentFlow.ACTIONLESS);
                        break;
                    case PaymentTypes.PaymentResult.Refused:
                    default:
                        var ref5;
                        throw new CreateOrderError({
                            status: CreateOrderErrors.ORDER_CREATION_CALL_FAILED,
                            label: CheckoutErrorsMapping.SomethingWentWrong,
                            cartHash: orderResponse.cartHash,
                            cartId: orderResponse.cartId,
                            type: orderResponse === null || orderResponse === void 0 ? void 0 : (ref5 = orderResponse.paymentResponse) === null || ref5 === void 0 ? void 0 : ref5.paymentMethod
                        });
                }
                setLoading(false);
                return orderResponse;
            } catch (e) {
                console.error(`Order creation failed for ${orderPayload.cartId}`);
                setLoading(false);
                if (e instanceof CreateOrderError) {
                    onError && onError(e.status, e === null || e === void 0 ? void 0 : e.label);
                }
                setError(CreateOrderErrors.ORDER_CREATION_CALL_FAILED);
                if (e instanceof CreateOrderError) {
                    var ref6;
                    if ((e === null || e === void 0 ? void 0 : e.cartHash) && (e === null || e === void 0 ? void 0 : e.cartId)) {
                        setContext && setContext({
                            cartId: e === null || e === void 0 ? void 0 : e.cartId,
                            cartHash: e === null || e === void 0 ? void 0 : e.cartHash
                        });
                    }
                    paymentHref && replace(appendQueryParamToUrl(paymentHref, {
                        cartHash: e === null || e === void 0 ? void 0 : e.cartHash,
                        cartId: e === null || e === void 0 ? void 0 : e.cartId,
                        type: e === null || e === void 0 ? void 0 : (ref6 = e.type) === null || ref6 === void 0 ? void 0 : ref6.toUpperCase(),
                        status: PaymentTypes.PaymentResult.Failed
                    }));
                }
            }
        });
        return function(orderPayload) {
            return _ref.apply(this, arguments);
        };
    }(), [
        isLoggedIn,
        apiConfig,
        cart,
        replace,
        onError,
        paymentHref,
        setContext
    ]);
    return [
        createOrder,
        {
            loading,
            error,
            orderResponse: orderResponse1,
            paymentFlow,
            setPaymentFlow
        }, 
    ];
};

export { useCreateOrder };
//# sourceMappingURL=use-create-order.js.map
